import React from 'react';
import { motion } from 'framer-motion';
import { Globe, BookOpen } from 'lucide-react';

const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
  </svg>
);

const LinkedinIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
    <rect x="2" y="9" width="4" height="12"></rect>
    <circle cx="4" cy="4" r="2"></circle>
  </svg>
);

const links = [
  {
    name: 'Instagram',
    url: 'https://www.instagram.com/ieeetemsvit?igsh=Y3ZrdjJ3cTB0M3do',
    icon: InstagramIcon,
  },
  {
    name: 'Website',
    url: 'https://ieeetemsvit.in',
    icon: Globe,
  },
  {
    name: 'Medium Blogs',
    url: 'https://ieee-tems-blogs.medium.com/',
    icon: BookOpen,
  },
  {
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/company/ieee-tems/',
    icon: LinkedinIcon,
  }
];

export function LinktreeCard() {
  return (
    <div className="absolute inset-0 z-20 overflow-y-auto pointer-events-none flex flex-col">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full responsive-wrapper relative pointer-events-auto m-auto"
      >
        <div className="relative group">
          {/* Card glow effect - extremely subtle */}
          <motion.div
            className="glow-always absolute -inset-[1px] rounded-none opacity-0 group-hover:opacity-100 transition-opacity duration-1000"
            animate={{
              boxShadow: [
                "0 0 5px 1px rgba(21,178,175,0.02)",
                "0 0 10px 2px rgba(21,178,175,0.08)",
                "0 0 5px 1px rgba(21,178,175,0.02)"
              ]
            }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", repeatType: "mirror" }}
          />

          {/* Traveling light beam effect */}
          <div className="glow-always-beams absolute -inset-[1px] rounded-none overflow-hidden transition-opacity duration-1000 opacity-50 group-hover:opacity-100">
            {/* Top light beam */}
            <motion.div
              className="absolute top-0 left-0 h-[3px] w-[50%] bg-gradient-to-r from-transparent via-[#15B2AF] to-transparent"
              initial={{ filter: "blur(2px)" }}
              animate={{ left: ["-50%", "100%"], opacity: [0.1, 0.4, 0.1], filter: ["blur(1px)", "blur(2.5px)", "blur(1px)"] }}
              transition={{ left: { duration: 3, ease: "easeInOut", repeat: Infinity, repeatDelay: 1 }, opacity: { duration: 1.5, repeat: Infinity, repeatType: "mirror" }, filter: { duration: 1.5, repeat: Infinity, repeatType: "mirror" } }}
            />

            {/* Right light beam */}
            <motion.div
              className="absolute top-0 right-0 h-[50%] w-[3px] bg-gradient-to-b from-transparent via-[#15B2AF] to-transparent"
              initial={{ filter: "blur(2px)" }}
              animate={{ top: ["-50%", "100%"], opacity: [0.1, 0.4, 0.1], filter: ["blur(1px)", "blur(2.5px)", "blur(1px)"] }}
              transition={{ top: { duration: 3, ease: "easeInOut", repeat: Infinity, repeatDelay: 1, delay: 0.75 }, opacity: { duration: 1.5, repeat: Infinity, repeatType: "mirror", delay: 0.75 }, filter: { duration: 1.5, repeat: Infinity, repeatType: "mirror", delay: 0.75 } }}
            />

            {/* Bottom light beam */}
            <motion.div
              className="absolute bottom-0 right-0 h-[3px] w-[50%] bg-gradient-to-r from-transparent via-[#15B2AF] to-transparent"
              initial={{ filter: "blur(2px)" }}
              animate={{ right: ["-50%", "100%"], opacity: [0.1, 0.4, 0.1], filter: ["blur(1px)", "blur(2.5px)", "blur(1px)"] }}
              transition={{ right: { duration: 3, ease: "easeInOut", repeat: Infinity, repeatDelay: 1, delay: 1.5 }, opacity: { duration: 1.5, repeat: Infinity, repeatType: "mirror", delay: 1.5 }, filter: { duration: 1.5, repeat: Infinity, repeatType: "mirror", delay: 1.5 } }}
            />

            {/* Left light beam */}
            <motion.div
              className="absolute bottom-0 left-0 h-[50%] w-[3px] bg-gradient-to-b from-transparent via-[#15B2AF] to-transparent"
              initial={{ filter: "blur(2px)" }}
              animate={{ bottom: ["-50%", "100%"], opacity: [0.1, 0.4, 0.1], filter: ["blur(1px)", "blur(2.5px)", "blur(1px)"] }}
              transition={{ bottom: { duration: 3, ease: "easeInOut", repeat: Infinity, repeatDelay: 1, delay: 2.25 }, opacity: { duration: 1.5, repeat: Infinity, repeatType: "mirror", delay: 2.25 }, filter: { duration: 1.5, repeat: Infinity, repeatType: "mirror", delay: 2.25 } }}
            />

            {/* Subtle corner glow spots */}
            <motion.div className="absolute top-0 left-0 h-[5px] w-[5px] rounded-full bg-[#15B2AF]/30 blur-[1px]" animate={{ opacity: [0.1, 0.3, 0.1] }} transition={{ duration: 2, repeat: Infinity, repeatType: "mirror" }} />
            <motion.div className="absolute top-0 right-0 h-[8px] w-[8px] rounded-full bg-[#15B2AF]/40 blur-[2px]" animate={{ opacity: [0.1, 0.3, 0.1] }} transition={{ duration: 2.4, repeat: Infinity, repeatType: "mirror", delay: 0.5 }} />
            <motion.div className="absolute bottom-0 right-0 h-[8px] w-[8px] rounded-full bg-[#15B2AF]/40 blur-[2px]" animate={{ opacity: [0.1, 0.3, 0.1] }} transition={{ duration: 2.2, repeat: Infinity, repeatType: "mirror", delay: 1 }} />
            <motion.div className="absolute bottom-0 left-0 h-[5px] w-[5px] rounded-full bg-[#15B2AF]/30 blur-[1px]" animate={{ opacity: [0.1, 0.3, 0.1] }} transition={{ duration: 2.3, repeat: Infinity, repeatType: "mirror", delay: 1.5 }} />
          </div>

          {/* Card border glow */}
          <div className="glow-always absolute -inset-[0.5px] rounded-none bg-gradient-to-r from-[#15B2AF]/5 via-[#15B2AF]/15 to-[#15B2AF]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />

          {/* Glass card background - Strong popout effect */}
          <div className="relative bg-[#0a0f0c]/60 backdrop-blur-[12px] rounded-none responsive-card border border-white/[0.08] shadow-[0px_30px_60px_-12px_rgba(2,26,25,0.9),0px_10px_35px_-8px_rgba(21,178,175,0.28)] overflow-hidden">

            {/* Subtle card inner patterns */}
            <div className="absolute inset-0 opacity-[0.03]"
              style={{ backgroundImage: `linear-gradient(135deg, white 0.5px, transparent 0.5px), linear-gradient(45deg, white 0.5px, transparent 0.5px)`, backgroundSize: '30px 30px' }}
            />

            {/* Header section */}
            <div className="text-center responsive-header relative z-10">
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", duration: 0.8 }}
                className="mx-auto responsive-logo bg-black/50 border border-[#15B2AF]/20 flex items-center justify-center relative overflow-hidden rounded-full shadow-lg"
              >
                <img
                  src="/logo.png"
                  alt="IEEE TEMS Logo"
                  className="w-full h-full object-cover relative z-10"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden absolute inset-0 items-center justify-center text-lg font-bold bg-clip-text text-transparent bg-gradient-to-b from-white to-white/70 text-center p-2 z-10">
                  IEEE<br />TEMS
                </div>
                {/* Inner lighting effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#15B2AF]/10 to-transparent opacity-80" />
              </motion.div>

              <div>
                <motion.h1
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="responsive-title font-bold bg-clip-text text-transparent bg-gradient-to-b from-white to-white/80"
                >
                  IEEE TEMS VIT
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-[#15B2AF]/80 responsive-subtitle mt-2"
                >
                  Technology and Engineering Management Society
                </motion.p>
              </div>
            </div>

            {/* Links section */}
            <div className="responsive-links-container relative z-10">
              {links.map((link, index) => {
                const Icon = link.icon;
                return (
                  <motion.a
                    key={link.name}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + index * 0.1, type: 'spring', stiffness: 100 }}
                    className="group relative flex items-center responsive-link-item bg-white/5 border border-transparent hover:border-white/20 responsive-link-hover hover:shadow-[0_8px_18px_-3px_rgba(26,26,26,1)] hover:z-20 rounded-none overflow-hidden transition-all duration-500 ease-in-out"
                  >
                    {/* Button background animation from prompt */}
                    <div className="link-bg absolute -inset-[0.5px] bg-gradient-to-r from-white/10 via-white/5 to-white/10 rounded-none opacity-0 group-hover:opacity-100 transition-all duration-500 ease-in-out" />

                    <div className="link-icon relative z-10 flex items-center justify-center shrink-0 responsive-link-icon bg-white/5 text-white/40 group-hover:text-[#15B2AF] transition-colors duration-500 ease-in-out">
                      <Icon className="responsive-link-icon-svg" />
                    </div>
                    <span className="link-label relative z-10 responsive-link-text min-w-0 truncate font-semibold text-white/80 group-hover:text-white tracking-wide transition-colors duration-500 ease-in-out">
                      {link.name}
                    </span>
                    <div className="link-arrow relative z-10 ml-auto pl-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 text-[#15B2AF]">
                      <svg className="responsive-link-icon-svg transform group-hover:translate-x-1 transition-transform duration-500 ease-in-out" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>

                    {/* Input highlight effect from prompt */}
                    <motion.div
                      className="link-glow absolute inset-0 bg-[#15B2AF]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-in-out"
                    />
                  </motion.a>
                );
              })}
            </div>

          </div>
        </div>
      </motion.div>
    </div>
  );
}
